import React, { useState } from "react";
import { AlertCircle } from "lucide-react";

const InlineError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 mt-1 text-xs text-red-400">
      <AlertCircle size={11} />
      {msg}
    </p>
  ) : null;

const AIStep1_Configure = ({
  jobRole, setJobRole,
  minExp, setMinExp,
  maxExp, setMaxExp,
  onNext,
  isLoading,
}) => {
  const [errors, setErrors] = useState({ jobRole: "", exp: "" });

  const validate = () => {
    const next = { jobRole: "", exp: "" };
    if (!jobRole.trim()) next.jobRole = "Job role is required";
    if (isNaN(minExp) || isNaN(maxExp) || maxExp <= minExp)
      next.exp = "Maximum experience must be greater than minimum";
    setErrors(next);
    return !next.jobRole && !next.exp;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-8">
      {/* Left: Form panel */}
      <div className="flex-1 rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-8 space-y-6">
        <div>
          <h3 className="text-base font-semibold dark:text-white text-textPrimary mb-1">
            Tell us about the role
          </h3>
          <p className="text-xs dark:text-gray-400 text-textSecondary">
            AI will use this to suggest relevant topics and generate tailored questions.
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium dark:text-gray-300 text-textSecondary">
            Job Role <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Senior Software Engineer"
            value={jobRole}
            onChange={(e) => { setJobRole(e.target.value); setErrors((p) => ({ ...p, jobRole: "" })); }}
            className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl w-full px-4 py-3 text-sm outline-none transition-colors"
          />
          <InlineError msg={errors.jobRole} />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium dark:text-gray-300 text-textSecondary">
            Experience Range (years) <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-1">
              <span className="text-xs dark:text-gray-500 text-textSecondary">Min</span>
              <input
                type="number"
                min="0"
                value={minExp}
                onChange={(e) => { setMinExp(parseInt(e.target.value) || 0); setErrors((p) => ({ ...p, exp: "" })); }}
                className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl w-full px-4 py-3 text-sm outline-none transition-colors"
              />
            </div>
            <span className="dark:text-gray-500 text-textSecondary text-sm mt-4">to</span>
            <div className="flex-1 space-y-1">
              <span className="text-xs dark:text-gray-500 text-textSecondary">Max</span>
              <input
                type="number"
                min="0"
                value={maxExp}
                onChange={(e) => { setMaxExp(parseInt(e.target.value) || 0); setErrors((p) => ({ ...p, exp: "" })); }}
                className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl w-full px-4 py-3 text-sm outline-none transition-colors"
              />
            </div>
          </div>
          <InlineError msg={errors.exp} />
        </div>

        <button
          onClick={handleNext}
          disabled={isLoading}
          className="button w-full flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Fetching Topics...</span>
            </>
          ) : (
            <span>Suggest Topics →</span>
          )}
        </button>
      </div>

      {/* Right: How it works info panel */}
      <div className="lg:w-80 rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-6 space-y-5 self-start">
        <h4 className="text-sm font-semibold dark:text-white text-textPrimary">How it works</h4>
        <ol className="space-y-4">
          {[
            { step: "1", title: "Configure", desc: "Enter the job role and experience range to tailor the question difficulty." },
            { step: "2", title: "Pick Topics", desc: "AI suggests relevant skill topics. Add, remove, or drag to reorder them." },
            { step: "3", title: "Review Questions", desc: "Browse the AI-generated MCQs and delete any that don't fit." },
            { step: "4", title: "Save Set", desc: "Name your MCQ set and optionally set a time limit before saving." },
          ].map(({ step, title, desc }) => (
            <li key={step} className="flex gap-3">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white">
                {step}
              </span>
              <div>
                <p className="text-xs font-semibold dark:text-gray-200 text-textPrimary">{title}</p>
                <p className="text-xs dark:text-gray-400 text-textSecondary leading-relaxed">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default AIStep1_Configure;
